import React from "react";
import { useTranslation } from "react-i18next";
import { safeHref } from "../../lib/safeHref";

const ProjectsGrid = ({ id, items, ...rest }) => {
  const { t } = useTranslation();
  const data = Array.isArray(items) ? items : [];

  // No venues → render nothing. The readiness gate in pages/RSVP.jsx requires
  // venueName + churchLocation, so this branch is defensive: never surface
  // sample venue cards as if they were the couple's real ones.
  if (data.length === 0) return null;

  const resolveTitle = (venue) => venue.title;
  const resolveDescription = (venue) => venue.description;

  const renderVenueInfo = (project, isHidden, isDroite = false) => {
    const safeMapUrl = safeHref(project.mapUrl);
    const description = resolveDescription(project);
    return (
    <div
      className={`ab-button-div ${isDroite ? "droite" : ""} ${
        isHidden ? "w-condition-invisible" : ""
      }`}
    >
      <div
        className="venue-info-block"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          textAlign: isDroite ? "right" : "left",
        }}
      >
        <div
          className="venue-description"
          style={{
            fontSize: "clamp(14px, 1.2vw, 18px)",
            lineHeight: "1.6",
            color: "var(--black)",
            opacity: 0.75,
            fontStyle: "italic",
          }}
        >
          {description}
        </div>
        {safeMapUrl ? (
          <a
            href={safeMapUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="button venue-map-link"
            style={{
              display: "flex",
              alignItems: "center",
              textAlign: isDroite ? "right" : "left",
              justifyContent: isDroite ? "flex-end" : "flex-start",
              textDecoration: "none",
              color: "inherit",
              cursor: "pointer",
            }}
          >
            <div className="button-icon" style={{ marginTop: "2px" }}>
              <img
                src="/assets/photos/Button-Icon.svg"
                alt={t("venues.mapPinAlt")}
                loading="lazy"
                className="b-icon"
              />
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                marginLeft: "12px",
              }}
            >
              <div
                className="button-txt"
                style={{
                  textTransform: "none",
                  lineHeight: "1.2",
                  paddingBottom: 0,
                  borderBottom: "none",
                  fontSize: "1.2em",
                  fontWeight: 500,
                }}
              >
                {project.location || description}
              </div>
              <div
                style={{
                  fontSize: "0.8em",
                  opacity: 0.6,
                  marginTop: "6px",
                  letterSpacing: "0.05em",
                  textTransform: "uppercase",
                }}
              >
                {t("venues.viewOnMap")}
              </div>
            </div>
          </a>
        ) : (
          <div
            className="button"
            style={{
              display: "flex",
              alignItems: "center",
              textAlign: isDroite ? "right" : "left",
              justifyContent: isDroite ? "flex-end" : "flex-start",
            }}
          >
            <div className="button-icon" style={{ marginTop: "2px" }}>
              <img
                src="/assets/photos/Button-Icon.svg"
                alt={t("venues.buttonIconAlt")}
                loading="lazy"
                className="b-icon"
              />
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                marginLeft: "12px",
              }}
            >
              <div
                className="button-txt"
                style={{
                  textTransform: "none",
                  lineHeight: "1.2",
                  borderBottom: "none",
                }}
              >
                {project.location || description}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    );
  };

  return (
    <section
      id={id}
      className="section topless m-100"
      style={{ paddingBottom: "20px" }}
      {...rest}
    >
      <div className="container w-dyn-list">
        <div role="list" className="real-list w-dyn-items">
          {data.map((venue, index) => {
            const isRightAligned = index % 2 !== 0;
            const title = resolveTitle(venue);
            return (
              <div key={index} role="listitem" className="real-item w-dyn-item">
                <div className="real-center-link w-inline-block">
                  <div
                    className={`real-half-bg ${
                      isRightAligned ? "w-condition-invisible" : ""
                    }`}
                  >
                    <div
                      style={{ backgroundImage: `url("${venue.image}")` }}
                      className="real-bg-im"
                    ></div>
                  </div>
                  <div
                    className={`real-half-bg droite ${
                      !isRightAligned ? "w-condition-invisible" : ""
                    }`}
                  >
                    <div
                      style={{ backgroundImage: `url("${venue.image}")` }}
                      className="real-bg-im droite"
                    ></div>
                  </div>
                  <div className="rel-content-wrap">
                    <div className="real-center-frame">
                      <img
                        loading="lazy"
                        alt={title}
                        src={venue.image}
                        className="real-center-im"
                      />
                    </div>
                    <div
                      className={`corner-no ${
                        isRightAligned ? "w-condition-invisible" : ""
                      }`}
                    >
                      <div className="no-txt">{venue.number}</div>
                    </div>
                    <div
                      className={`corner-no droite ${
                        !isRightAligned ? "w-condition-invisible" : ""
                      }`}
                    >
                      <div className="no-txt droite">{venue.number}</div>
                    </div>
                    <div
                      className={`real-name-div ${
                        isRightAligned ? "w-condition-invisible" : ""
                      }`}
                    >
                      <div className="side-link">
                        <div>{title}</div>
                      </div>
                    </div>
                    <div
                      className={`real-name-div droite ${
                        !isRightAligned ? "w-condition-invisible" : ""
                      }`}
                    >
                      <div className="side-link droite">
                        <div>{title}</div>
                      </div>
                    </div>
                    {renderVenueInfo(venue, isRightAligned)}
                    {renderVenueInfo(venue, !isRightAligned, true)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ProjectsGrid;
